import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
} from '@material-ui/core';
import { UPDATE_SETUP } from '../../../store/setup';
import { NotificationType, BidProcessType } from '../../../../Enums';
import AutoCompleteSelect from './AutoCompleteSelect';
import TimedNotificationSelect from './TimedNotificationSelect';
import CustomTagsDisplay from './CustomTagsDisplay';
import '../SetupEdit.scss';

/**
 * @param {{ stepIndex: number }} props
 */
function NotificationSection({ stepIndex }) {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'admin.setupEdit.notificationStep' });
    /** @type {ShiftBidding.AdminStore} */
    // @ts-ignore
    const { adminSetup, adminAppData } = useSelector((state) => state);
    const { notificationTemplates, bidProcessType } = adminSetup;
    const { genericNotificationTemplates } = adminAppData;
    const isPreferential = bidProcessType === BidProcessType.Preferential;

    /**
     * @param {string} target
     * @param {Setup.INotificationTemplate[]} value
     */
    const updateSetupStore = (target, value) => {
        dispatch({
            type: UPDATE_SETUP,
            payload: {
                [target]: value,
            },
        });
    };

    /**
     * @type {{[Key: number]: Setup.INotificationTemplate}}
     */
    const selectedNotifs = notificationTemplates.reduce((prev, curr) => ({ ...prev, [curr.type]: curr }), {});

    const updateNotifications = (/** @type {NotificationType} */ type, /** @type {number | string} */ templateId) => {
        const indexToUpdate = notificationTemplates.findIndex((x) => x.type === type);
        notificationTemplates[indexToUpdate].templateId = templateId;
        updateSetupStore('notificationTemplates', notificationTemplates);
    };

    /**
     * @param {NotificationType} type
     * @param {number} value
     */
    const handleTimeValueChange = (type, value) => {
        const indexToUpdate = notificationTemplates.findIndex((x) => x.type === type);
        notificationTemplates[indexToUpdate].timeValue = parseInt(value, 10);
        updateSetupStore('notificationTemplates', notificationTemplates);
    };

    /**
     * @param {NotificationType} type
     * @param {TimeUnit} value
     */
    const handleTimeUnitChange = (type, value) => {
        const indexToUpdate = notificationTemplates.findIndex((x) => x.type === type);
        notificationTemplates[indexToUpdate].timeUnit = value;
        updateSetupStore('notificationTemplates', notificationTemplates);
    };

    const renderPreferentialEmployeeNotifications = () => (
        <>
            <TimedNotificationSelect
                label={t('preferentialWillStartNotifIdToEmployee')}
                placeholderLabel={t('selectPlaceholder')}
                id="pref-employee-notif-1"
                genericNotificationTemplates={genericNotificationTemplates}
                selectedNotif={selectedNotifs[NotificationType.preferentialWillStartNotifIdToEmployee]}
                handleUpdateNotification={(
                    /** @type {number | string} */templateId,
                ) => updateNotifications(NotificationType.preferentialWillStartNotifIdToEmployee, templateId)}
                handleUpdateTimeValue={(
                    /** @type {number} */ value,
                ) => handleTimeValueChange(NotificationType.preferentialWillStartNotifIdToEmployee, value)}
                handleUpdateTimeUnit={(
                    /** @type {TimeUnit} */ unit,
                ) => handleTimeUnitChange(NotificationType.preferentialWillStartNotifIdToEmployee, unit)}
            />
            <AutoCompleteSelect
                label={t('preferentialHasStartedNotifIdToEmployee')}
                placeholderLabel={t('selectPlaceholder')}
                id="pref-employee-notif-2"
                selectedNotif={selectedNotifs[NotificationType.preferentialHasStartedNotifIdToEmployee]}
                handleUpdateNotification={(
                    /** @type {number | string} */templateId,
                ) => updateNotifications(NotificationType.preferentialHasStartedNotifIdToEmployee, templateId)}
                genericNotificationTemplates={genericNotificationTemplates}
            />
            <TimedNotificationSelect
                label={t('preferentialDraftMadeNotifIdToEmployee')}
                placeholderLabel={t('selectPlaceholder')}
                id="pref-employee-notif-3"
                genericNotificationTemplates={genericNotificationTemplates}
                selectedNotif={selectedNotifs[NotificationType.preferentialDraftMadeNotifIdToEmployee]}
                handleUpdateNotification={(
                    /** @type {number | string} */templateId,
                ) => updateNotifications(NotificationType.preferentialDraftMadeNotifIdToEmployee, templateId)}
                handleUpdateTimeValue={(
                    /** @type {number} */ value,
                ) => handleTimeValueChange(NotificationType.preferentialDraftMadeNotifIdToEmployee, value)}
                handleUpdateTimeUnit={(
                    /** @type {TimeUnit} */ unit,
                ) => handleTimeUnitChange(NotificationType.preferentialDraftMadeNotifIdToEmployee, unit)}
            />
            <TimedNotificationSelect
                label={t('willEndNotifIdToEmployee')}
                placeholderLabel={t('selectPlaceholder')}
                id="pref-employee-notif-4"
                genericNotificationTemplates={genericNotificationTemplates}
                selectedNotif={selectedNotifs[NotificationType.willEndNotifIdToEmployee]}
                handleUpdateNotification={(
                    /** @type {number | string} */templateId,
                ) => updateNotifications(NotificationType.willEndNotifIdToEmployee, templateId)}
                handleUpdateTimeValue={(
                    /** @type {number} */ value,
                ) => handleTimeValueChange(NotificationType.willEndNotifIdToEmployee, value)}
                handleUpdateTimeUnit={(
                    /** @type {TimeUnit} */ unit,
                ) => handleTimeUnitChange(NotificationType.willEndNotifIdToEmployee, unit)}
            />
            <AutoCompleteSelect
                label={t('hasEndedNotifIdToEmployee')}
                placeholderLabel={t('selectPlaceholder')}
                id="pref-employee-notif-5"
                selectedNotif={selectedNotifs[NotificationType.hasEndedNotifIdToEmployee]}
                handleUpdateNotification={(
                    /** @type {number | string} */templateId,
                ) => updateNotifications(NotificationType.hasEndedNotifIdToEmployee, templateId)}
                genericNotificationTemplates={genericNotificationTemplates}
            />
            <AutoCompleteSelect
                label={t('approvedNotifIdToEmployee')}
                placeholderLabel={t('selectPlaceholder')}
                id="pref-employee-notif-6"
                selectedNotif={selectedNotifs[NotificationType.approvedNotifIdToEmployee]}
                handleUpdateNotification={(
                    /** @type {number | string} */templateId,
                ) => updateNotifications(NotificationType.approvedNotifIdToEmployee, templateId)}
                genericNotificationTemplates={genericNotificationTemplates}
            />
        </>
    );
    const renderSequentialEmployeeNotifications = () => (
        <>
            <TimedNotificationSelect
                label={t('sequentialTurnWillStartNotifIdToEmployee')}
                placeholderLabel={t('selectPlaceholder')}
                id="seq-employee-notif-1"
                genericNotificationTemplates={genericNotificationTemplates}
                selectedNotif={selectedNotifs[NotificationType.sequentialTurnWillStartNotifIdToEmployee]}
                handleUpdateNotification={(
                    /** @type {number | string} */templateId,
                ) => updateNotifications(NotificationType.sequentialTurnWillStartNotifIdToEmployee, templateId)}
                handleUpdateTimeValue={(
                    /** @type {number} */ value,
                ) => handleTimeValueChange(NotificationType.sequentialTurnWillStartNotifIdToEmployee, value)}
                handleUpdateTimeUnit={(
                    /** @type {TimeUnit} */ unit,
                ) => handleTimeUnitChange(NotificationType.sequentialTurnWillStartNotifIdToEmployee, unit)}
            />
            <AutoCompleteSelect
                label={t('sequentialTurnHasStartedNotifIdToEmployee')}
                placeholderLabel={t('selectPlaceholder')}
                id="seq-employee-notif-2"
                selectedNotif={selectedNotifs[NotificationType.sequentialTurnHasStartedNotifIdToEmployee]}
                handleUpdateNotification={(
                    /** @type {number | string} */templateId,
                ) => updateNotifications(NotificationType.sequentialTurnHasStartedNotifIdToEmployee, templateId)}
                genericNotificationTemplates={genericNotificationTemplates}
            />
            <AutoCompleteSelect
                label={t('sequentialTurnHasEndedNotifIdToEmployee')}
                placeholderLabel={t('selectPlaceholder')}
                id="seq-employee-notif-3"
                selectedNotif={selectedNotifs[NotificationType.sequentialTurnHasEndedNotifIdToEmployee]}
                handleUpdateNotification={(
                    /** @type {number | string} */templateId,
                ) => updateNotifications(NotificationType.sequentialTurnHasEndedNotifIdToEmployee, templateId)}
                genericNotificationTemplates={genericNotificationTemplates}
            />
            <TimedNotificationSelect
                label={t('willEndNotifIdToEmployee')}
                placeholderLabel={t('selectPlaceholder')}
                id="seq-employee-notif-4"
                genericNotificationTemplates={genericNotificationTemplates}
                selectedNotif={selectedNotifs[NotificationType.willEndNotifIdToEmployee]}
                handleUpdateNotification={(
                    /** @type {number | string} */templateId,
                ) => updateNotifications(NotificationType.willEndNotifIdToEmployee, templateId)}
                handleUpdateTimeValue={(
                    /** @type {number} */ value,
                ) => handleTimeValueChange(NotificationType.willEndNotifIdToEmployee, value)}
                handleUpdateTimeUnit={(
                    /** @type {TimeUnit} */ unit,
                ) => handleTimeUnitChange(NotificationType.willEndNotifIdToEmployee, unit)}
            />
            <AutoCompleteSelect
                label={t('hasEndedNotifIdToEmployee')}
                placeholderLabel={t('selectPlaceholder')}
                id="seq-employee-notif-5"
                selectedNotif={selectedNotifs[NotificationType.hasEndedNotifIdToEmployee]}
                handleUpdateNotification={(
                    /** @type {number | string} */templateId,
                ) => updateNotifications(NotificationType.hasEndedNotifIdToEmployee, templateId)}
                genericNotificationTemplates={genericNotificationTemplates}
            />
            <AutoCompleteSelect
                label={t('approvedNotifIdToEmployee')}
                placeholderLabel={t('selectPlaceholder')}
                id="seq-employee-notif-6"
                selectedNotif={selectedNotifs[NotificationType.approvedNotifIdToEmployee]}
                handleUpdateNotification={(
                    /** @type {number | string} */templateId,
                ) => updateNotifications(NotificationType.approvedNotifIdToEmployee, templateId)}
                genericNotificationTemplates={genericNotificationTemplates}
            />
        </>
    );

    return (
        <Card className="card">
            <CardHeader
                className="card--header"
                gutterBottom
                avatar={<Avatar className="avatar">{stepIndex}</Avatar>}
                title={t('title')}
            />
            <CardContent>
                <CustomTagsDisplay />
                <Card className="card">
                    <CardHeader
                        disableTypography
                        className="card--header"
                        title={t('manager')}
                    />
                    <CardContent>
                        <TimedNotificationSelect
                            label={t('willStartNotifIdToManager')}
                            placeholderLabel={t('selectPlaceholder')}
                            id="manager-notif-1"
                            genericNotificationTemplates={genericNotificationTemplates}
                            selectedNotif={selectedNotifs[NotificationType.willStartNotifIdToManager]}
                            handleUpdateNotification={(
                            /** @type {number | string} */templateId,
                            ) => updateNotifications(NotificationType.willStartNotifIdToManager, templateId)}
                            handleUpdateTimeValue={(
                            /** @type {number} */ value,
                            ) => handleTimeValueChange(NotificationType.willStartNotifIdToManager, value)}
                            handleUpdateTimeUnit={(
                            /** @type {TimeUnit} */ unit,
                            ) => handleTimeUnitChange(NotificationType.willStartNotifIdToManager, unit)}
                        />
                        <AutoCompleteSelect
                            label={t('hasStartedNotifIdToManager')}
                            placeholderLabel={t('selectPlaceholder')}
                            id="manager-notif-2"
                            selectedNotif={selectedNotifs[NotificationType.hasStartedNotifIdToManager]}
                            handleUpdateNotification={(
                            /** @type {number | string} */templateId,
                            ) => updateNotifications(NotificationType.hasStartedNotifIdToManager, templateId)}
                            genericNotificationTemplates={genericNotificationTemplates}
                        />
                        <TimedNotificationSelect
                            label={t('willEndNotifIdToManager')}
                            placeholderLabel={t('selectPlaceholder')}
                            id="manager-notif-3"
                            genericNotificationTemplates={genericNotificationTemplates}
                            selectedNotif={selectedNotifs[NotificationType.willEndNotifIdToManager]}
                            handleUpdateNotification={(
                            /** @type {number | string} */templateId,
                            ) => updateNotifications(NotificationType.willEndNotifIdToManager, templateId)}
                            handleUpdateTimeValue={(
                            /** @type {number} */ value,
                            ) => handleTimeValueChange(NotificationType.willEndNotifIdToManager, value)}
                            handleUpdateTimeUnit={(
                            /** @type {TimeUnit} */ unit,
                            ) => handleTimeUnitChange(NotificationType.willEndNotifIdToManager, unit)}
                        />
                        <AutoCompleteSelect
                            label={t('hasEndedNotifIdToManager')}
                            placeholderLabel={t('selectPlaceholder')}
                            id="manager-notif-4"
                            selectedNotif={selectedNotifs[NotificationType.hasEndedNotifIdToManager]}
                            handleUpdateNotification={(
                            /** @type {number | string} */templateId,
                            ) => updateNotifications(NotificationType.hasEndedNotifIdToManager, templateId)}
                            genericNotificationTemplates={genericNotificationTemplates}
                        />
                        <AutoCompleteSelect
                            label={t('autoApprovedNotifIdToManager')}
                            placeholderLabel={t('selectPlaceholder')}
                            id="manager-notif-5"
                            selectedNotif={selectedNotifs[NotificationType.autoApprovedNotifIdToManager]}
                            handleUpdateNotification={(
                            /** @type {number | string} */templateId,
                            ) => updateNotifications(NotificationType.autoApprovedNotifIdToManager, templateId)}
                            genericNotificationTemplates={genericNotificationTemplates}
                        />
                        <AutoCompleteSelect
                            label={t('autoSignOffAndPublishNotifIdToManager')}
                            placeholderLabel={t('selectPlaceholder')}
                            id="manager-notif-17"
                            selectedNotif={selectedNotifs[NotificationType.autoSignOffAndPublishNotifIdToManager]}
                            handleUpdateNotification={(
                            /** @type {number | string} */templateId,
                            ) => updateNotifications(NotificationType.autoSignOffAndPublishNotifIdToManager, templateId)}
                            genericNotificationTemplates={genericNotificationTemplates}
                        />
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardHeader
                        disableTypography
                        className="card--header"
                        gutterBottom
                        title={t('employee')}
                    />
                    <CardContent>
                        {isPreferential
                            ? renderPreferentialEmployeeNotifications()
                            : renderSequentialEmployeeNotifications()}
                        <AutoCompleteSelect
                            label={t('refusedNotifIdToEmployee')}
                            placeholderLabel={t('selectPlaceholder')}
                            id="common-employee-notif-1"
                            selectedNotif={selectedNotifs[NotificationType.refusedNotifIdToEmployee]}
                            handleUpdateNotification={(
                            /** @type {number | string} */templateId,
                            ) => updateNotifications(NotificationType.refusedNotifIdToEmployee, templateId)}
                            genericNotificationTemplates={genericNotificationTemplates}
                        />
                        <AutoCompleteSelect
                            label={t('unawardedNotifIdToEmployee')}
                            placeholderLabel={t('selectPlaceholder')}
                            id="common-employee-notif-2"
                            selectedNotif={selectedNotifs[NotificationType.unawardedNotifIdToEmployee]}
                            handleUpdateNotification={(
                            /** @type {number | string} */templateId,
                            ) => updateNotifications(NotificationType.unawardedNotifIdToEmployee, templateId)}
                            genericNotificationTemplates={genericNotificationTemplates}
                        />
                        <AutoCompleteSelect
                            label={t('cancelledNotifIdToEmployee')}
                            placeholderLabel={t('selectPlaceholder')}
                            id="common-employee-notif-3"
                            selectedNotif={selectedNotifs[NotificationType.cancelledNotifIdToEmployee]}
                            handleUpdateNotification={(
                            /** @type {number | string} */templateId,
                            ) => updateNotifications(NotificationType.cancelledNotifIdToEmployee, templateId)}
                            genericNotificationTemplates={genericNotificationTemplates}
                        />
                        <AutoCompleteSelect
                            label={t(`${isPreferential ? 'preferential' : 'sequential'}RankChangeNotifIdToEmployee`)}
                            placeholderLabel={t('selectPlaceholder')}
                            id="common-employee-notif-3"
                            selectedNotif={selectedNotifs[NotificationType.rankChangeNotifIdToEmployee]}
                            handleUpdateNotification={(
                            /** @type {number | string} */templateId,
                            ) => updateNotifications(NotificationType.rankChangeNotifIdToEmployee, templateId)}
                            genericNotificationTemplates={genericNotificationTemplates}
                        />
                        <AutoCompleteSelect
                            label={t('removedFromBidNotifIdToEmployee')}
                            placeholderLabel={t('selectPlaceholder')}
                            id="common-employee-notif-3"
                            selectedNotif={selectedNotifs[NotificationType.removedFromBidNotifIdToEmployee]}
                            handleUpdateNotification={(
                            /** @type {number | string} */templateId,
                            ) => updateNotifications(NotificationType.removedFromBidNotifIdToEmployee, templateId)}
                            genericNotificationTemplates={genericNotificationTemplates}
                        />
                    </CardContent>
                </Card>
            </CardContent>
        </Card>
    );
}

export default NotificationSection;
